import { useMutation } from '@tanstack/react-query';
import { FileText, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { confirmStudySourceText, type StudySession, type StudySourceTextResult } from './studyApi';
import {
  PII_LABELS,
  piiReviewOf,
  sourceTextErrorMessage,
  type PiiReview,
} from './studySourceTextReview';

/**
 * "Check the text" step (SIR-QA-07, SIR-TRU-02; study-institution-m1-design.md §10.3). The learner
 * confirms the text of the source before Tutor or generated practice use it. When the server finds
 * personal details, the learner sees what would be taken out and either edits the text or uses the
 * text without those details. Only the server decides what counts as a personal detail.
 */

interface StudySourceTextCheckProps {
  sessionId: string;
  confirmation: StudySession['source_confirmation'];
  /** The server requires this step before Tutor and generated practice. */
  required: boolean;
  onConfirmed: (session: StudySourceTextResult) => void;
}

export function StudySourceTextCheck({
  sessionId,
  confirmation,
  required,
  onConfirmed,
}: StudySourceTextCheckProps) {
  const [draft, setDraft] = useState(confirmation?.text ?? '');
  const [review, setReview] = useState<PiiReview | null>(null);
  const [redactedNotice, setRedactedNotice] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(confirmation?.text ?? '');
  }, [confirmation?.text]);

  const confirm = useMutation({
    mutationFn: ({ text, accept }: { text: string; accept: boolean }) =>
      confirmStudySourceText(sessionId, text, accept),
    onSuccess: (updated) => {
      setReview(null);
      setRedactedNotice(updated.pii?.redacted === true);
      setDraft(updated.source_confirmation?.text ?? '');
      onConfirmed(updated);
    },
    onError: (error, { text }) => setReview(piiReviewOf(error, text)),
  });

  const submit = () => {
    setRedactedNotice(false);
    confirm.mutate({ text: draft.trim(), accept: false });
  };

  return (
    <section
      className="rounded-3xl border border-hairline bg-canvas p-4"
      aria-label="Confirmed source text"
      data-testid="study-source-text-check"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-canvas-pure text-brand-sky">
          <FileText className="h-4 w-4" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-extrabold text-ink">Check the text</h3>
          <p className="mt-1 text-xs font-medium leading-relaxed text-slate2">
            {required ? 'Needed before AI Tutor and practice. ' : 'Optional. '}
            Fix anything that looks wrong.
          </p>
        </div>
      </div>
      <textarea
        ref={textarea}
        aria-label="Readable source text"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          setReview(null);
        }}
        rows={3}
        maxLength={4000}
        placeholder="Type the question, formula or notes visible in the source…"
        className="mt-3 block w-full resize-y rounded-2xl border border-hairline bg-canvas-pure px-4 py-3 text-sm font-medium text-ink outline-none focus:border-brand-sky"
      />
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-xs font-bold text-slate2">
          {confirmation
            ? 'Learner confirmed · edit anytime'
            : required
              ? 'Not confirmed yet · needed before AI Tutor'
              : 'Not confirmed yet'}
        </p>
        <button
          type="button"
          disabled={confirm.isPending || draft.trim().length === 0}
          onClick={submit}
          className="rounded-full bg-ink px-4 py-2 text-xs font-extrabold text-white disabled:opacity-40"
        >
          {confirm.isPending ? 'Saving…' : confirmation ? 'Update text' : 'Confirm text'}
        </button>
      </div>

      {review && (
        <div
          role="region"
          aria-label="Personal details found"
          className="mt-3 rounded-2xl bg-wash-sunshine p-4"
        >
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            This text looks like it has personal details
          </p>
          <ul className="mt-2 list-disc pl-5 text-xs font-bold text-ink">
            {review.findings.map((finding) => (
              <li key={finding.type}>
                {PII_LABELS[finding.type] ?? 'Personal detail'}
                {finding.count > 1 ? ` (${finding.count})` : ''}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] font-extrabold uppercase tracking-[0.1em] text-slate2">
            What Tutor will see
          </p>
          <p
            data-testid="study-source-redacted-preview"
            className="mt-1 whitespace-pre-wrap rounded-2xl border border-hairline bg-canvas-pure px-3 py-2 text-sm font-medium text-ink"
          >
            {review.redactedText}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={confirm.isPending}
              onClick={() => confirm.mutate({ text: review.text, accept: true })}
              className="rounded-full bg-ink px-4 py-2 text-xs font-extrabold text-white disabled:opacity-40"
            >
              Use text without these details
            </button>
            <button
              type="button"
              onClick={() => {
                setReview(null);
                textarea.current?.focus();
              }}
              className="rounded-full border border-hairline bg-canvas-pure px-4 py-2 text-xs font-extrabold text-ink"
            >
              Edit my text
            </button>
          </div>
        </div>
      )}

      {(redactedNotice || (confirmation?.redacted === true && !review)) && (
        <p role="status" className="mt-3 text-xs font-bold text-slate2">
          Personal details were taken out before saving.
        </p>
      )}
      {confirm.error && !review && (
        <p role="alert" className="mt-3 text-xs font-bold text-danger-600">
          {sourceTextErrorMessage(confirm.error)}
        </p>
      )}
    </section>
  );
}
